import { createContext, useContext } from 'react';
import toast from 'react-hot-toast';

const ToastContext = createContext(null);

const baseToastStyle = {
  padding: '14px 14px',
  borderRadius: '12px',
  background: '#fff',
  color: '#111',
  boxShadow: '0 10px 30px rgba(0,0,0,0.12)',
  minWidth: '260px',
};

export const ToastProvider = ({ children }) => {
  const notifyLoading = (msg, opts = {}) =>
    toast.loading(msg, { position: 'top-center', style: baseToastStyle, ...opts });

  const notifySuccess = (msg, opts = {}) =>
    toast.success(msg, { position: 'top-center', style: baseToastStyle, ...opts });

  // Same message → same toast id, so repeated failures (retries, double effects) show one toast.
  const notifyError = (msg, opts = {}) =>
    toast.error(msg, {
      id: typeof msg === 'string' ? `err:${msg}` : undefined,
      position: 'top-center',
      style: baseToastStyle,
      ...opts,
    });

  const notifyCustom = (msg, opts = {}) =>
    toast(msg, { position: 'top-center', style: baseToastStyle, ...opts });

  const notifyDismiss = (id) => toast.dismiss(id);
  const notifyDismissAll = () => toast.dismiss();

  // Update existing toast (useful for manual async flows)
  const notifyUpdate = (id, type, msg, opts = {}) => {
    const common = { id, position: 'top-center', style: baseToastStyle, ...opts };
    if (type === 'success') return toast.success(msg, common);
    if (type === 'error') return toast.error(msg, common);
    if (type === 'loading') return toast.loading(msg, common);
    return toast(msg, common);
  };

  const notifyPromise = (promise, msgs, opts = {}) =>
    toast.promise(promise, msgs, {
      position: 'top-center',
      style: baseToastStyle,
      ...opts,
    });

  const notifyConfirm = (
    {
      title = 'Confirm action',
      message = 'Are you sure?',
      confirmText = 'Confirm',
      cancelText = 'Cancel',
      variant = 'danger', // danger | primary | success
    },
    onConfirm,
    onCancel
  ) => {
    toast(
      (t) => (
        <div style={{ width: 320 }}>
          <div className="fw-semibold" style={{ fontSize: 14 }}>
            {title}
          </div>
          <div className="text-muted mt-1" style={{ fontSize: 13, lineHeight: 1.4 }}>
            {message}
          </div>

          <div className="d-flex gap-2 justify-content-end mt-3">
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => {
                toast.dismiss(t.id);
                onCancel?.();
              }}
            >
              {cancelText}
            </button>

            <button
              className={`btn btn-sm btn-${variant}`}
              onClick={() => {
                toast.dismiss(t.id);
                onConfirm?.();
              }}
            >
              {confirmText}
            </button>
          </div>
        </div>
      ),
      { duration: 10000, position: 'top-center', style: baseToastStyle }
    );
  };

  return (
    <ToastContext.Provider
      value={{
        notifySuccess,
        notifyError,
        notifyLoading,
        notifyCustom,
        notifyConfirm,
        notifyDismiss,
        notifyDismissAll,
        notifyPromise,
        notifyUpdate,
      }}
    >
      {children}
    </ToastContext.Provider>
  );
};

export const useToast = () => useContext(ToastContext);
